import React from 'react'
import {
    Box, InputAdornment, LinearProgress, MenuItem, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TablePagination, TextField, Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined'
import { ApiPushGetRecipients } from '../../apiService/Core/Apicall'
import PushPanel from './PushPanel'
import { InlineAlert } from './PushDisabledNotice'
import RecipientDialog from './RecipientDialog'
import StatusPill from './StatusPill'
import { useGuardedFetch } from './useGuardedFetch'
import { formFieldSx, paginationStyle, searchFieldStyle, selectProps, tableSx, tdStyle, thStyle, trHover } from './pushStyles'
import { fmtDate, getErrorMessage, POLL_INTERVAL_MS, qs, useDebouncedValue } from './pushUtils'
import type { Pagination, Recipient } from './types'

interface Props {
    campaignId: string
    /** Poll while the campaign is still running. */
    polling?: boolean
    /** Changing this value forces a refetch (e.g. after retry). */
    refreshKey?: number
}

const STATUS_FILTERS = [
    { value: '', label: 'All statuses' },
    { value: 'SENT', label: 'Sent (accepted by Firebase)' },
    { value: 'FAILED', label: 'Failed' },
    { value: 'SKIPPED', label: 'Skipped' },
    { value: 'PENDING', label: 'Pending' },
]

const HEADERS = ['User', 'Email', 'Audience source', 'Eligible devices', 'Attempted', 'Accepted', 'Failed devices', 'Status', 'Failure / skip reason', 'Last attempt', 'Retry']

const RecipientTable: React.FC<Props> = ({ campaignId, polling, refreshKey = 0 }) => {
    const [status, setStatus] = React.useState('')
    const [search, setSearch] = React.useState('')
    const [page, setPage] = React.useState(0)
    const [rowsPerPage, setRowsPerPage] = React.useState(20)
    const [selectedUser, setSelectedUser] = React.useState<string | null>(null)
    const q = useDebouncedValue(search.trim())

    const fetcher = React.useCallback(
        (silent: boolean) =>
            ApiPushGetRecipients(campaignId, qs({ status, q, page: page + 1, limit: rowsPerPage }), { silent }),
        [campaignId, status, q, page, rowsPerPage]
    )
    const { data, error, loading, refetch } = useGuardedFetch<any>(fetcher, {
        pollMs: polling ? POLL_INTERVAL_MS : 0,
        silentLoads: true,
    })

    const firstRender = React.useRef(true)
    React.useEffect(() => {
        if (firstRender.current) { firstRender.current = false; return }
        refetch()
    }, [refreshKey, refetch])

    const rows: Recipient[] = data?.data?.items ?? []
    const pagination: Pagination | undefined = data?.meta?.pagination
    const total = pagination?.total ?? 0

    // keep the page valid if the result set shrinks
    React.useEffect(() => {
        if (data && page > 0 && rows.length === 0) setPage(Math.max(0, Math.ceil(total / rowsPerPage) - 1))
    }, [data, page, rows.length, total, rowsPerPage])

    return (
        <PushPanel
            title="Recipients"
            icon={<PeopleAltOutlinedIcon sx={{ color: '#ff6a3d', fontSize: 20 }} />}
            sx={{ p: 0 }}
            bodySx={{ p: 0 }}
            action={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <TextField
                        select size="small" value={status}
                        onChange={(e) => { setStatus(e.target.value); setPage(0) }}
                        sx={{ ...formFieldSx, minWidth: 220, '& .MuiOutlinedInput-root': { ...formFieldSx['& .MuiOutlinedInput-root'], borderRadius: '7px' } }}
                        slotProps={{ select: selectProps }}
                    >
                        {STATUS_FILTERS.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                    </TextField>
                    <TextField
                        placeholder="Search name, email or user ID"
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(0) }}
                        size="small"
                        sx={{ ...searchFieldStyle, width: 280 }}
                        slotProps={{ input: { startAdornment: (<InputAdornment position="start"><SearchIcon className="color-primary" /></InputAdornment>) } }}
                    />
                </Box>
            }
        >
            {error && <Box sx={{ p: 2 }}><InlineAlert severity="error">{getErrorMessage(error)}</InlineAlert></Box>}
            <Box sx={{ height: 3 }}>{loading && <LinearProgress sx={{ height: 3, background: 'transparent', '& .MuiLinearProgress-bar': { background: '#ff6a3d' } }} />}</Box>
            <TableContainer sx={{ maxHeight: '60vh' }}>
                <Table stickyHeader sx={tableSx}>
                    <TableHead>
                        <TableRow>{HEADERS.map((h) => <TableCell key={h} sx={thStyle}>{h}</TableCell>)}</TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length > 0 ? rows.map((r) => (
                            <TableRow key={r.userId} sx={{ ...trHover, cursor: 'pointer' }} onClick={() => setSelectedUser(r.userId)}>
                                <TableCell sx={tdStyle}>
                                    <Typography fontSize={13} fontWeight={600} color="#e0e0e0">{r.name || r.userId}</Typography>
                                </TableCell>
                                <TableCell sx={tdStyle}>{r.email || '-'}</TableCell>
                                <TableCell sx={{ ...tdStyle, maxWidth: 200, whiteSpace: 'normal' }}>{r.sources?.length ? r.sources.join(', ') : '-'}</TableCell>
                                <TableCell sx={tdStyle}>{r.devices?.eligible ?? 0}</TableCell>
                                <TableCell sx={tdStyle}>{r.devices?.attempted ?? 0}</TableCell>
                                <TableCell sx={tdStyle}>{r.devices?.accepted ?? 0}</TableCell>
                                <TableCell sx={tdStyle}>{r.devices?.failed ?? 0}</TableCell>
                                <TableCell sx={tdStyle}><StatusPill status={r.status} /></TableCell>
                                <TableCell sx={tdStyle}>{r.failureReason || r.skipReason || '-'}</TableCell>
                                <TableCell sx={tdStyle}>{fmtDate(r.lastAttemptAt)}</TableCell>
                                <TableCell sx={tdStyle}><StatusPill status={r.retryState} /></TableCell>
                            </TableRow>
                        )) : (
                            <TableRow>
                                <TableCell colSpan={HEADERS.length} align="center" sx={{ py: 8, background: '#1a1a1a', border: 'none' }}>
                                    <PeopleAltOutlinedIcon sx={{ color: '#2f2f2f', fontSize: 40, mb: 1 }} />
                                    <Typography color="#4a4a4a" fontSize={13}>
                                        {loading ? 'Loading recipients...' : 'No recipients match the current filters.'}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div" count={total} page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
                rowsPerPageOptions={[10, 20, 50, 100]} showFirstButton showLastButton
                sx={paginationStyle}
            />
            {selectedUser && (
                <RecipientDialog key={selectedUser} campaignId={campaignId} userId={selectedUser} onClose={() => setSelectedUser(null)} />
            )}
        </PushPanel>
    )
}

export default RecipientTable
